import { Box } from "@mui/system";
import { PatientInfoAndFormsPageStyles } from "./PatientInfoAndFormsPageStyles";
import { PatientCard } from "@components/cards/PatientCard";
import { AssignFormsStepper } from "@components/stepper/AssignFormsStepper";
import { IInOfficeEvent } from "../../../types/formAssignments/IInOfficeEvent";
import useOnAssignForms from "@hooks/useOnAssignForms";
import type { FormAssignmentsPage } from "../FormAssignmentsWrapper";
import PageLayout from "@components/pageLayout/PageLayout";
import FormSelectionAndView from "./forms/FormSelectionAndView";
import useInitAllInOfficeEvents from "./useInitAllInOfficeEvents";
import usePendingFormDataToAssign from "./forms/usePendingFormDataToAssign";
import FamilyMemberSelector from "./familyMembers/familyMemberSelector";
import { useState } from "react";
import { useGetFeatureFlag } from "@hooks/useGetFeatureFlag";
import { EFeatureFlags } from "../../../enums/EFeatureFlags";
import { AppointmentDropdown } from "@components/appointmentDropDown/AppointmentDropDown";

interface PatientInfoAndFormsPageProps {
  initialEvent: IInOfficeEvent;
  setPage: (page: FormAssignmentsPage) => void;
}

const PatientInfoAndFormsPage = ({
  initialEvent,
  setPage,
}: PatientInfoAndFormsPageProps) => {
  const {
    pendingFormDataToAssign,
    handleFormSelection,
    handleDeleteForm,
    handlePackageSelection,
    initDataFromAllInOfficeEvents,
    handleInOfficeEventChange,
    totalIncompleteFormsCount,
  } = usePendingFormDataToAssign(initialEvent);

  const { allInOfficeEvents, isLoading: isLoadingAllInOfficeEvents } =
    useInitAllInOfficeEvents({
      initialInOfficeEvent: initialEvent,
      onComplete: initDataFromAllInOfficeEvents,
    });

  const [selectedEvent, setSelectedEvent] =
    useState<IInOfficeEvent>(initialEvent);

  const { isEnabled: isFamilyFeatureEnabled } = useGetFeatureFlag(
    EFeatureFlags.FastrAppFamilyEnabled
  );

  const { patient } = selectedEvent;

  const { onAssignForms, isLoading: isLoadingAssignForms } = useOnAssignForms();

  return (
    <PageLayout
      primaryButtonConfig={{
        label: "Assign",
        onClick: () => onAssignForms(pendingFormDataToAssign),
        disabled:
          totalIncompleteFormsCount === 0 ||
          isLoadingAssignForms ||
          isLoadingAllInOfficeEvents,
      }}
      secondaryButtonConfig={{
        label: "Back",
        onClick: () => setPage("patientList"),
        disabled: false,
      }}
    >
      <AssignFormsStepper
        title="Assign Forms"
        stepOne="Choose patient"
        stepTwo={
          isFamilyFeatureEnabled ? "Select appt and forms" : "Select forms"
        }
        currentStepNumber={2}
      />
      <Box sx={PatientInfoAndFormsPageStyles}>
        <PatientCard
          dob={patient.dateOfBirth}
          chartNumber={patient.chartId}
          patientName={`${patient.firstName} ${patient.lastName}`}
          packageName={selectedEvent.formAssignment?.packageName}
          totalForms={0}
          age={patient.age}
          np={patient.isNewPatient}
          avatar={patient.avatar}
          noBorder={true}
          width={77}
          height={77}
        />
        {isFamilyFeatureEnabled && (
          <FamilyMemberSelector
            selectedEvent={selectedEvent}
            pendingFormDataToAssign={pendingFormDataToAssign}
            onFamilyMemberSelect={(patientId) =>
              setSelectedEvent(pendingFormDataToAssign[patientId].inOfficeEvent)
            }
          />
        )}
        {isFamilyFeatureEnabled && (
          <AppointmentDropdown
            options={allInOfficeEvents[selectedEvent.patient.id].map(
              (event) => ({
                value: event.uniqueKey,
                label: (() => {
                  const timeValue =
                    event.appointment?.startsAt ??
                    event.formAssignment?.dateCreated;
                  if (!timeValue) return "Unknown time";

                  return new Date(timeValue * 1000).toLocaleTimeString(
                    "en-US",
                    {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    }
                  );
                })(),
              })
            )}
            selected={selectedEvent.uniqueKey}
            setSelected={(value) => {
              const event = allInOfficeEvents[selectedEvent.patient.id].find(
                (event) => event.uniqueKey === value
              )!;
              setSelectedEvent(event);
              handleInOfficeEventChange(event);
            }}
          />
        )}
        <FormSelectionAndView
          handleFormSelection={(form) => handleFormSelection(patient.id, form)}
          handleDeleteForm={(formId) => handleDeleteForm(patient.id, formId)}
          handlePackageSelection={(formPackage) =>
            handlePackageSelection(patient.id, formPackage)
          }
          selectedEvent={selectedEvent}
          incompleteForms={
            pendingFormDataToAssign[selectedEvent.patient.id].incompleteForms
          }
        />
      </Box>
    </PageLayout>
  );
};

export default PatientInfoAndFormsPage;
